// EventLandingPage — /event/:eventId
//
// WS-A Chunk 4 UI. The publish/paid-checkout return home for a single
// event. Four shapes:
//
//   • Draft, viewer is owner        → <EventDraftAdmin /> (Details =
//                                     PayAndCreate) + tabs for roster,
//                                     groups, games, league
//   • Published, viewer is owner    → status/share card + <EventDraftAdmin />
//                                     with tabs still active so the owner
//                                     can edit roster / groups / games
//                                     after publish (player drop-and-swap,
//                                     weather reschedule, add a game, etc.)
//   • Draft, viewer is NOT owner    → "This event isn't published yet."
//   • Published, viewer is NOT owner→ confirmation + shareable link
//
// Also handles the two Stripe return-URL flags:
//   ?published=1  → post-checkout success; shows a welcome card
//   ?draft=1      → checkout was cancelled; harmless, we just render the
//                   PayAndCreate again

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../lib/db'
import EventDraftAdmin from '../components/EventDraftAdmin'

function fmtDate(d) {
  if (!d) return null
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function EventLandingPage() {
  const { eventId } = useParams()
  const { player } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const params = new URLSearchParams(location.search)
  const justPublished = params.get('published') === '1'

  const shareUrl = useMemo(() => {
    if (!event) return null
    return `${window.location.origin}/event/${event.id}`
  }, [event])

  const refresh = async () => {
    setLoading(true)
    try {
      const { data } = await db('get_event_by_id', { event_id: eventId })
      if (!data) {
        setNotFound(true)
      } else {
        setEvent(data)
      }
    } catch {
      setNotFound(true)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!eventId) return
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  // If the user just returned from Stripe (published=1), the webhook may
  // not have flipped published_at yet. Retry the fetch a couple times so
  // the UI catches up without a manual refresh.
  useEffect(() => {
    if (!justPublished || !event || event.published_at) return
    const timer = setTimeout(refresh, 1500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justPublished, event?.published_at])

  const isOwner = !!(event && player && event.owner_id === player.id)

  if (loading) {
    return (
      <div className="page">
        <div className="container"><p className="text-muted">Loading…</p></div>
      </div>
    )
  }

  if (notFound || !event) {
    return (
      <div className="page">
        <div className="container">
          <div className="page-header">
            <p className="eyebrow">Event</p>
            <h1>Not found</h1>
          </div>
          <div className="card">
            <p>We couldn't find that event. It may have been deleted, or you might have followed an old link.</p>
            <button className="btn btn-ghost btn-full" style={{ marginTop: 12 }} onClick={() => navigate('/')}>
              ← Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  const published = !!event.published_at

  // Primary bottom button. Prefer a quick "← Back" to the previous in-app page
  // (e.g. the Events list) — but this page is also reached in two ways where
  // navigate(-1) is WRONG: (1) an external share link opened in a fresh tab has
  // no prior SPA entry, so navigate(-1) is a dead no-op; (2) the Stripe
  // post-checkout return (?published=1) lands here after a full-page load from
  // Stripe's domain, so navigate(-1) would bounce back to the expired checkout.
  // React Router tags the initial/fresh-load entry with key 'default'; combined
  // with the justPublished flag, those two cases fall back to Home. The label
  // tracks the action so the button never looks dead.
  const canGoBack = !justPublished && location.key !== 'default'

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <p className="eyebrow">Event</p>
          <h1>{event.name || 'Untitled event'}</h1>
          {event.year && (
            <p className="text-muted text-sm">{event.year}</p>
          )}
        </div>

        {published && justPublished && (
          <div className="card" style={{ marginBottom: 16, borderColor: 'var(--green-deep)' }}>
            <h3 style={{ margin: 0, marginBottom: 6 }}>Published.</h3>
            <p className="text-muted text-sm" style={{ margin: 0 }}>
              Your event is live. Share the link below with your players.
            </p>
          </div>
        )}

        {published && (
          <div className="card" style={{ marginBottom: 16 }}>
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Status</p>
            <p style={{ margin: 0, fontWeight: 600 }}>
              Published {fmtDate(event.published_at)}
            </p>
            {event.paid_amount_cents > 0 && (
              <p className="text-muted text-xs" style={{ marginTop: 4 }}>
                Organizer fee paid: ${(event.paid_amount_cents / 100).toFixed(2)}
              </p>
            )}
            {shareUrl && (
              <>
                <hr className="divider" />
                <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Share link</p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="input"
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}
                  />
                  <button
                    className="btn btn-ghost"
                    onClick={() => { navigator.clipboard?.writeText(shareUrl) }}
                  >
                    Copy
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Owner admin surface: renders whether draft OR published so the
            organizer can edit roster / groups / games / league config after
            publish (player swap, weather reschedule, add-a-game, etc.).
            EventDraftAdmin reads event.published_at to decide what the event
            details section shows (publish flow vs. read-only summary). */}
        {isOwner && (
          <EventDraftAdmin event={event} onChanged={() => refresh()} />
        )}

        {!published && !isOwner && (
          <div className="card">
            <p style={{ margin: 0 }}>
              This event isn't published yet.
            </p>
            <p className="text-muted text-sm" style={{ marginTop: 6 }}>
              The organizer hasn't finished setup. Check back with them for a share link.
            </p>
          </div>
        )}

        <button
          className="btn btn-ghost btn-full"
          style={{ marginTop: 16 }}
          onClick={() => (canGoBack ? navigate(-1) : navigate('/'))}
        >
          {canGoBack ? '← Back' : '← Home'}
        </button>
      </div>
    </div>
  )
}
